import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import MonitorLpBlock from "@/components/feature/MonitorLpBlock";
import {
  FeatureList,
  TreatmentIndexNav,
  TreatmentSection,
} from "@/pages/treatment/components/TreatmentSections";
import { DowntimeTable, FlowSteps, MethodTable, RiskList } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentCta, TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import SkinTreatmentHero from "./SkinTreatmentHero";
import SkinDetailPrice from "./SkinDetailPrice";
import { PlaceholderPanel, PlaceholderSlots, SkinConcernLinks } from "./SkinPlaceholder";
import { skinTreatmentDetails } from "@/mocks/skinTreatmentDetails";
import type { SkinTreatment } from "@/mocks/skinTreatments";
import { isPendingValue } from "@/utils/isPendingContent";

const flowSteps = [
  { no: "01", title: "ご予約", text: "WEB予約・LINE・お電話より、ご希望の日時をお知らせください。" },
  { no: "02", title: "カウンセリング", text: "お悩みやご希望、生活スタイルを丁寧にお伺いします。" },
  { no: "03", title: "医師の診察", text: "肌状態を確認し、適した方法をご提案します。" },
  { no: "04", title: "施術", text: "内容・料金・リスクについてご説明し、ご納得のうえで施術を行います。" },
  { no: "05", title: "アフターケア", text: "施術後の経過を確認し、ホームケアをご案内します。" },
];

/** 施術情報が未設定の場合に表示する既定値（推測値は入れず「—」を維持） */
const DEFAULT_SPEC = [
  { label: "施術時間", value: "—" },
  { label: "麻酔", value: "—" },
  { label: "ダウンタイム", value: "—" },
  { label: "推奨回数", value: "—" },
  { label: "推奨間隔", value: "—" },
];

/** 正式なFAQが未設定の施術に表示する汎用FAQ */
const DEFAULT_FAQS = [
  {
    q: "この施術はどのような悩みに向いていますか？",
    a: "正式な適応や施術内容が確定し次第、本ページに掲載します。まずはカウンセリングでご相談ください。",
  },
  {
    q: "カウンセリングだけでも相談できますか？",
    a: "はい。施術を決めていない段階でのご相談も歓迎しています。",
  },
  {
    q: "料金はどこで確認できますか？",
    a: "料金は本ページ下部の料金表、またはカウンセリングにてご案内します。",
  },
  {
    q: "予約の変更・キャンセルはできますか？",
    a: "前日までにご連絡いただければ承ります。",
  },
];

const ABOUT_NOTE =
  "本施術の正式な概要・目的・適応などは現在準備中です。確定し次第、このセクションに掲載します。";
const FEATURES_NOTE = "本施術の特徴は正式情報が確定し次第、以下の枠に掲載します。";
const METHOD_NOTE = "施術内容・治療方法の正式な情報は現在準備中です。確定し次第、以下の枠に掲載します。";
const RISK_NOTE = "本施術のリスク・副作用・注意事項は、正式情報が確定し次第掲載します。";

/**
 * ダーマペン4の新規お試しモニター専用LPへの導線。
 * SBDCの価格掲載ルール（公式サイト＝定価 / LP＝新規お試しモニター価格）に従い、
 * 公式サイト内にはモニター価格を掲載せず、LPへの入口のみを設置する。
 * ダーマペン4を施術方法に用いる3施術（シンデレラ系・ヴェルヴェットスキン）のみに表示する。
 */
const DERMAPEN4_MONITOR_SLUGS = ["cinderella-stemsup", "cinderella-diamond", "velvet-skin"];
const DERMAPEN4_MONITOR_LP_URL = "https://sbscl.biz/dermapen4/";

export default function SkinTreatmentDetail({ data }: { data: SkinTreatment }) {
  const detail = skinTreatmentDetails[data.slug];
  const specItems = detail ? detail.spec : DEFAULT_SPEC;
  // 未確定（「—」等）の項目は患者向けに表示しない
  const specRows = specItems.filter((item) => !isPendingValue(item.value));
  // 施術情報のうち未確定（「使用機器：確認中」等）は行ごと非表示にする
  const methodItems = detail?.method?.filter((item) => !isPendingValue(item.value));

  const indexItems = [
    { label: "この施術について", target: "st-about" },
    { label: "このようなお悩みの方へ", target: "st-concerns" },
    { label: "施術の特徴", target: "st-features" },
    { label: "施術情報", target: "st-method" },
    { label: "施術の流れ", target: "st-flow" },
    ...(specRows.length > 0 ? [{ label: "施術時間・ダウンタイム", target: "st-spec" }] : []),
    { label: "リスク・注意事項", target: "st-risk" },
    { label: "料金", target: "st-price" },
    { label: "Q&A", target: "st-faq" },
  ];

  return (
    <>
      <SkinTreatmentHero data={data} />
      <TreatmentIndexNav items={indexItems} />

      {/* ABOUT */}
      <TreatmentSection id="st-about" en="ABOUT" jp="この施術について" tone="plain">
        {detail && detail.about.length > 0 ? (
          <Reveal>
            <div className="max-w-3xl space-y-6">
              {detail.about.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-[14px] leading-[2.1] text-foreground-700 md:text-[15px]"
                >
                  {paragraph}
                </p>
              ))}
              {detail.aboutNote ? (
                <p className="border-l-2 border-accent-500 pl-4 font-heading text-[14px] leading-[2] text-foreground-900 md:text-[15px]">
                  {detail.aboutNote}
                </p>
              ) : null}
            </div>
          </Reveal>
        ) : (
          <PlaceholderPanel label="CONTENT PENDING" note={ABOUT_NOTE} />
        )}
      </TreatmentSection>

      {/* CONCERNS */}
      <TreatmentSection
        id="st-concerns"
        en="CONCERNS"
        jp="このようなお悩みの方へ"
        lead="お肌管理ページで本施術をご案内しているお悩みです。"
        tone="tint"
      >
        {data.concerns.length > 0 ? (
          <Reveal>
            <SkinConcernLinks items={data.concerns} />
          </Reveal>
        ) : (
          <p className="max-w-2xl text-[13px] leading-[2.1] text-foreground-600">
            本施術の正式な適応は確定し次第、本ページに掲載します。まずはカウンセリングでご相談ください。
          </p>
        )}
      </TreatmentSection>

      {/* FEATURES */}
      <TreatmentSection id="st-features" en="FEATURES" jp="施術の特徴" tone="plain">
        {detail && detail.features ? (
          <FeatureList items={detail.features} />
        ) : (
          <Reveal>
            <PlaceholderSlots count={3} columns="sm:grid-cols-2 lg:grid-cols-3" />
            <p className="mt-6 max-w-2xl text-[12.5px] leading-[2] text-foreground-600">{FEATURES_NOTE}</p>
          </Reveal>
        )}
      </TreatmentSection>

      {/* METHOD */}
      <TreatmentSection id="st-method" en="METHOD" jp="施術情報" tone="tint">
        {methodItems && methodItems.length > 0 ? (
          <Reveal>
            <MethodTable items={methodItems} />
            {detail?.methodNote ? (
              <p className="mt-6 max-w-2xl text-[12.5px] leading-[2] text-foreground-600">
                {detail.methodNote}
              </p>
            ) : null}
          </Reveal>
        ) : (
          <Reveal>
            <PlaceholderPanel label="CONTENT PENDING" note={METHOD_NOTE} />
            <div className="mt-6">
              <PlaceholderSlots count={2} columns="sm:grid-cols-2" />
            </div>
          </Reveal>
        )}
      </TreatmentSection>

      {/* FLOW */}
      <TreatmentSection id="st-flow" en="FLOW" jp="施術の流れ" tone="plain">
        <FlowSteps items={flowSteps} />
      </TreatmentSection>

      {/* TIME / DOWNTIME（未確定項目は非表示。全項目が未確定の場合はセクションごと非表示） */}
      {specRows.length > 0 ? (
        <TreatmentSection id="st-spec" en="TIME / DOWNTIME" jp="施術時間・ダウンタイム等" tone="tint">
          <DowntimeTable items={specRows} />
        </TreatmentSection>
      ) : null}

      {/* RISK */}
      <TreatmentSection id="st-risk" en="RISK / NOTES" jp="リスク・副作用・注意事項" tone="plain">
        {detail && detail.risks ? (
          <Reveal>
            <RiskList
              items={detail.risks}
              note={detail.riskNote ?? "美容医療は自由診療です。施術前に医師より説明を行います。"}
            />
          </Reveal>
        ) : (
          <Reveal>
            <PlaceholderPanel label="CONTENT PENDING" note={RISK_NOTE} />
            <p className="mt-6 border-l-2 border-accent-500 pl-4 text-[12px] leading-[2] text-foreground-600">
              美容医療は自由診療です。施術前に医師より説明を行います。
            </p>
          </Reveal>
        )}
      </TreatmentSection>

      {/* PRICE */}
      <TreatmentSection id="st-price" en="PRICE" jp="料金" tone="tint">
        <SkinDetailPrice price={detail?.price} note={detail?.priceNote} />
      </TreatmentSection>

      {/* ダーマペン4 モニターLP導線（施術内容・料金表示の後に配置。対象施術のみ表示） */}
      {DERMAPEN4_MONITOR_SLUGS.includes(data.slug) ? (
        <section className="bg-background-50 py-16 md:py-20 lg:py-24">
          <div className="u-container">
            <MonitorLpBlock
              eyebrow="初めての方へ"
              title="ダーマペン4 モニターのご案内"
              description="初めて当院をご利用になる方を対象としたモニタープランをご用意しています。"
              label="モニタープランの詳細を見る"
              url={DERMAPEN4_MONITOR_LP_URL}
            />
          </div>
        </section>
      ) : null}

      {/* FAQ */}
      <TreatmentSection id="st-faq" en="Q&amp;A" jp="よくあるご質問" tone="plain">
        <div className="max-w-3xl">
          <TreatmentFaq items={detail?.faqs ?? DEFAULT_FAQS} />
        </div>
        <div className="mt-12">
          <Link
            to="/treatment/skin-management"
            className="group inline-flex items-center gap-2 whitespace-nowrap text-[13px] text-foreground-800 transition-colors hover:text-accent-700"
          >
            <i className="ri-arrow-left-line transition-transform group-hover:-translate-x-0.5" aria-hidden />
            お肌管理へ戻る
          </Link>
        </div>
      </TreatmentSection>

      <TreatmentCta lineLabel="お肌管理についてLINEで相談" treatmentJp={data.jp} />
    </>
  );
}